import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { IntersectionOptions } from 'react-intersection-observer';
import { useSearchBarContext } from 'src/components/search-bar/search-bar-context';
import { useEffectOnMount } from 'src/hooks/useEffectOnMount';
import { AppContext } from 'src/components/app-context';
import { ViewportContext } from 'src/components/viewport';
import { AdServiceContextT } from 'src/components/page/page-content';
import { withAds } from 'src/components/ads/context/withAds';
import SearchBar from 'src/components/search-bar';
import { setSearchBarInHero } from 'src/areas/search/actions/search';
import { Hero, SearchWrapper, Content, HeroTitle } from './style';
import { RecentSearches } from 'src/areas/search/components/recent-searches';
import { getRecentSearches } from 'src/areas/search-suggestions/history-storage';

type Props = {
  active?: boolean;
  ads?: any;
  adServiceContext: AdServiceContextT;
};

let ref: Element;

const SearchHeroComponent = ({ active = true, adServiceContext }: Props) => {
  const location = useLocation();

  const { market, t } = useContext(AppContext);
  const { isMobile } = useContext(ViewportContext);
  const { setFocused } = useSearchBarContext();

  const searchBarRef = useRef(null);
  const heroRef = useRef<HTMLDivElement>(null);

  const [isVisible, setIsVisible] = useState(true);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const isFirstPage = location.pathname === '/';
  const hasTakeoverAd = Boolean(adServiceContext.takeover);

  const dispatch = useDispatch();

  useEffect(() => {
    setFocused(!isMobile && isFirstPage);
  }, [isMobile]);

  const checkVisible = useCallback((entries: IntersectionObserverEntry[]) => {
    const showSearchBarInHero = Boolean(
      entries.filter(en => en.isIntersecting).length
    );

    setIsVisible(showSearchBarInHero);
    dispatch(setSearchBarInHero(showSearchBarInHero));
  }, []);

  useEffect(() => {
    const options: IntersectionOptions = {
      threshold: 0.9,
    };

    const observer = new IntersectionObserver(checkVisible, options);

    if (searchBarRef.current) {
      ref = searchBarRef.current;
    }

    observer.observe(ref);

    return () => {
      observer.unobserve(ref);
    };
  }, [checkVisible, searchBarRef]);

  useEffectOnMount(() => {
    setRecentSearches(getRecentSearches());
  });

  const marketCode = market.code.toLocaleLowerCase();
  const useSalesHero = marketCode === 'se' || marketCode === 'no';
  const useOneLineTitle = marketCode === 'se' || marketCode === 'no';
  const useBiggerFontSize = marketCode === 'no';

  const backgroundImage = useSalesHero
    ? `${market.cdn}/g/rfe/landing-page/hero-sales-image.png`
    : `${market.cdn}/g/rfe/landing-page/hero-search-and-stars.png`;
  const hasRecentSearches = recentSearches && recentSearches.length > 0;

  return (
    <Hero
      $active={active}
      $backgroundImage={backgroundImage}
      $fullWidth={hasTakeoverAd}
      $hasRecentSearches={hasRecentSearches}
      data-test="hero-search"
      ref={heroRef}
    >
      <Content $oneLineTitle={useOneLineTitle} ref={searchBarRef}>
        <HeroTitle
          $oneLineTitle={useOneLineTitle}
          $biggerFontSize={useBiggerFontSize}
        >
          {t('landing', 'hero.title')}
        </HeroTitle>
        <SearchWrapper>
          <SearchBar visible={isVisible} />
        </SearchWrapper>
      </Content>
      {hasRecentSearches && <RecentSearches recentSearches={recentSearches} />}
    </Hero>
  );
};

export const SearchHero = withAds(SearchHeroComponent);
